import { Toaster as SonnerToaster, toast } from 'sonner';

// Brand-styled Sonner. The lib is headless — these classes pull the design
// system through (paper card, ink border, hard-edge shadow, mono eyebrow tint
// per intent). Use `magToast.error/success/info` from anywhere; the visual
// language matches the rest of the dashboard.

export function Toaster() {
  return (
    <SonnerToaster
      position="top-right"
      offset={20}
      gap={10}
      duration={4200}
      visibleToasts={4}
      // Explicit dismiss. Sonner is swipe-dismissible by default, which is
      // undiscoverable — before this, a long toast just sat over the top-nav
      // until the 4.2s timer ran out, and a multi-line one covered the nav for
      // its whole life.
      closeButton
      toastOptions={{
        unstyled: true,
        classNames: {
          // Card chrome — ink border, 4px ink shadow. Tone is signaled by the
          // shadow color applied per-intent below.
          //
          // bg-white, NOT var(--paper): the page body is var(--cream) #f4efe6
          // and --paper is #faf7f0, ~2% apart, so a paper toast read as part
          // of the page instead of floating over it. White is the app's
          // existing floating-surface color — the user menu and the
          // notifications dropdown both use `border-[var(--ink)] bg-white
          // shadow-[4px_4px_0_var(--plum)]`, and they use --paper as the HOVER
          // tint inside themselves. The toast was painted in the hover tint.
          // `items-start` is load-bearing, for the same reason the close
          // button below needs `!self-start`. Sonner sets its own
          // `align-items: center` on the toast, but that rule — like every
          // [data-icon] / [data-content] / [data-button] rule it ships — is
          // gated on `[data-styled='true']`, and `unstyled: true` sets
          // data-styled="false". So NOTHING aligns the children and the flex
          // default `stretch` wins. The visible symptom is the action button
          // (btn-spell, yellow) stretching to the full height of the card —
          // next to a 200-char description that wraps to ~10 lines at 360px,
          // it renders as a tall yellow slab instead of a button.
          //
          // A COLLAPSED BACK TOAST SHOWS CHROME ONLY — and the two classes
          // that do it are the THIRD instance of the gating trap above, not a
          // style preference. Sonner ships two rules for a stacked toast that
          // is not in front:
          //
          //   styles.css:293  [data-expanded='false'][data-front='false']
          //                   { height: var(--front-toast-height) }
          //   styles.css:311  …[data-styled='true'] > * { opacity: 0 }
          //
          // The first is NOT gated and applies; the second IS gated and
          // `unstyled: true` kills it. So a back toast gets clamped to the
          // FRONT toast's height while its children stay fully visible, and a
          // back toast taller than the front one paints its text out of the
          // box — the founder's screenshot shows "publish-search-semilla-rg…"
          // lying across the stacked card edges below the front card.
          //
          // `[&>*]:opacity-0` restores sonner's rule verbatim, `> *` included:
          // that deliberately hides the close button too, because a dismiss
          // control on a toast you cannot read is not a control. Hovering sets
          // data-expanded='true', the selector stops matching, and every toast
          // is readable and dismissible again — the behaviour unstyled removed.
          //
          // `overflow-hidden` is the backstop. opacity alone fixes today's
          // symptom, but the box stays height-clamped, so anything sonner does
          // not hide (a pseudo-element, a child added later) would paint
          // outside the card again. It does not touch the 4px hard shadow,
          // which is drawn outside the border box.
          toast: [
            'pointer-events-auto flex w-[360px] items-start gap-3 p-4',
            'overflow-hidden',
            'data-[expanded=false]:data-[front=false]:[&>*]:opacity-0',
            'rounded-xl border-[1.5px] border-[var(--ink)] bg-white text-[var(--ink)]',
            'shadow-[4px_4px_0_var(--ink)]',
            'font-display',
          ].join(' '),
          // Top-RIGHT rather than sonner's default top-left: dismiss belongs
          // where a reader looks to close something, and the toast stack is
          // already right-aligned.
          //
          // Always visible rather than hover-revealed. The thing being fixed
          // is discoverability; a control you must hover to find does not fix
          // it, and it would be unreachable on touch.
          closeButton: [
            // `!static` FIRST, and it is load-bearing. Sonner injects
            // `[data-close-button]{position:absolute;left:var(--toast-close-
            // button-start);...}` into the document, and `unstyled: true` does
            // NOT remove it — unstyled drops sonner's class-based toast
            // styling, while this rule is an attribute selector that always
            // applies. Without the override the button leaves the flex flow,
            // every layout class below is silently inert, and it lands wherever
            // sonner's CSS vars put it (its default is the LEFT edge).
            //
            // Back in flow, `order-last ml-auto` puts it at the right edge
            // regardless of sonner's DOM order (it renders the button before
            // the content), and `self-start` keeps it level with the title.
            '!static !order-last !ml-auto !self-start',
            '!flex !h-6 !w-6 !shrink-0 !items-center !justify-center',
            '!rounded-full !border-[1.5px] !border-[var(--ink)] !bg-white',
            '!text-[var(--ink)] !opacity-100',
            'hover:!bg-[var(--spell)]',
            'focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-2',
            'focus-visible:!outline-[var(--plum)]',
            '!transition-colors',
          ].join(' '),
          title: 'text-[14px] font-semibold leading-snug tracking-[-0.01em]',
          description: 'mt-1 text-[13px] leading-snug text-[var(--ink)]/75',
          actionButton: 'btn btn-spell !px-3 !py-1.5 !text-[12px]',
          cancelButton: 'btn btn-ghost !px-3 !py-1.5 !text-[12px]',
          // Intent accents — flip the shadow color per tone so the toast
          // "leans" in the right direction without coloring the whole card.
          error: '!shadow-[4px_4px_0_var(--pulse)]',
          success: '!shadow-[4px_4px_0_var(--plum)]',
          warning: '!shadow-[4px_4px_0_var(--spell)]',
          info: '!shadow-[4px_4px_0_var(--plum)]',
          loading: '!shadow-[4px_4px_0_var(--ink)]',
        },
      }}
    />
  );
}

// Lowercase-by-default wrapper matches Mag's voice rules (§1: no title case).
// Use this everywhere instead of importing `toast` directly so we keep a
// single throat to choke for tone/voice/styling later.
export const magToast = {
  success: (message: string, description?: string) =>
    toast.success(message, description ? { description } : undefined),
  error: (message: string, description?: string) =>
    toast.error(message, description ? { description } : undefined),
  info: (message: string, description?: string) =>
    toast(message, description ? { description } : undefined),
  warning: (message: string, description?: string) =>
    toast.warning(message, description ? { description } : undefined),
  loading: (message: string) => toast.loading(message),
  dismiss: (id?: string | number) => toast.dismiss(id),
};
